<template>
    <div class="entrada">
        <div class="entrada__cartao">
            <h1 class="entrada__titulo">hLiberty · LPBuilder</h1>

            <template v-if="!local">
                <p class="entrada__texto">
                    {{ $t("components.lpbuilder.entrada.somenteAdmin") }}
                </p>
                <v-btn
                    color="primary"
                    variant="flat"
                    block
                    @click="irParaOAdmin"
                >
                    {{ $t("common.labels.backToAdmin") }}
                </v-btn>
            </template>

            <template v-else>
                <p class="entrada__texto">
                    {{ $t("components.lpbuilder.entrada.explicacao") }}
                </p>

                <v-alert
                    type="warning"
                    variant="tonal"
                    density="compact"
                    class="mb-4"
                >
                    {{ $t("components.lpbuilder.entrada.aviso") }}
                </v-alert>

                <v-alert
                    v-if="erro"
                    type="error"
                    variant="tonal"
                    density="compact"
                    class="mb-3"
                >
                    {{ erro }}
                </v-alert>

                <v-form @submit.prevent="entrar">
                    <v-text-field
                        v-model="usuarioId"
                        label="usuario_id"
                        density="compact"
                        variant="outlined"
                        class="mb-2"
                        autofocus
                        required
                    />
                    <v-text-field
                        v-model="corp"
                        label="corp"
                        density="compact"
                        variant="outlined"
                        class="mb-3"
                        required
                    />
                    <v-btn
                        type="submit"
                        color="primary"
                        variant="flat"
                        block
                        :loading="carregando"
                        :disabled="!usuarioId || !corp"
                    >
                        Entrar
                    </v-btn>
                </v-form>
            </template>
        </div>
    </div>
</template>

<script setup>
/**
 * Entrada local de desenvolvimento.
 *
 * Em ambiente publicado esta tela nao cria sessao nenhuma: ela so explica que
 * a entrada e pelo admin e oferece o caminho de volta. O formulario so
 * aparece quando **os dois lados** estao em modo local -- o /config desta
 * aplicacao e o LPBUILDER_LOGIN_LOCAL da API. Sem isso o endpoint responde 404.
 *
 * Diferente do deck (de onde este molde veio), a API do lpbuilder nao semeia uma
 * lista de pessoas para escolher -- `POST /api/dev/login` aceita qualquer
 * `usuario_id` (ver DevTokenIssuer no lpbuilder-api), entao a tela e so um
 * formulario de dois campos.
 *
 * O token que chega aqui e emitido pela API do lpbuilder com o mesmo formato
 * do liberty/auth (ver PlatformToken/DevTokenIssuer): assim o caminho
 * exercitado na maquina e o mesmo que roda no cluster, menos a origem do
 * token.
 */
import { ref } from "vue";
import { useRouter, useRoute } from "vue-router";
import { setAuth } from "@/services/api/auth.js";
import { entrarLocal } from "@/services/api/lpbuilder/sessao.js";
import { voltarParaOAdmin, DESTINO_PADRAO } from "@/services/sso.js";

const router = useRouter();
const route = useRoute();

const local = ref(!!window.LOGIN_LOCAL);
const usuarioId = ref("");
const corp = ref("local");
const carregando = ref(false);
const erro = ref("");

function irParaOAdmin() {
    voltarParaOAdmin({ motivo: "sem-sessao" });
}

async function entrar() {
    carregando.value = true;
    erro.value = "";

    try {
        const resposta = await entrarLocal({
            usuarioId: usuarioId.value,
            corp: corp.value,
        });

        setAuth({
            token: resposta.access_token,
            // O /me do liberty/auth nao existe no modo local: monta-se aqui o
            // minimo que `getUser()` le, a partir do que foi digitado.
            credentials: {
                user: {
                    id: usuarioId.value,
                    login: usuarioId.value,
                    name: usuarioId.value,
                    corps: {},
                },
                corp: corp.value,
            },
        });

        localStorage.setItem("corp", corp.value);

        await router.replace(route.query.de || DESTINO_PADRAO);
    } catch (e) {
        erro.value =
            e?.response?.data?.message ||
            "A API do LPBuilder nao esta com o login local ligado (LPBUILDER_LOGIN_LOCAL).";
    } finally {
        carregando.value = false;
    }
}
</script>

<style scoped lang="scss">
.entrada {
    min-height: 100vh;
    display: flex;
    align-items: center;
    justify-content: center;
    background: #f4f5f8;
    padding: 24px;

    &__cartao {
        background: #fff;
        border-radius: 14px;
        box-shadow: 0 10px 40px rgba(0, 0, 0, 0.08);
        padding: 28px;
        width: 100%;
        max-width: 420px;
    }

    &__titulo {
        font-size: 18px;
        margin: 0 0 6px;
    }

    &__texto {
        color: var(--secondaryColor);
        font-size: 14px;
        margin: 0 0 16px;
    }
}
</style>
